import React, { useState } from 'react';
import { AlertDialog, Badge, Button, Card, Flex, Tabs, Text } from '@radix-ui/themes';
import { LightningBoltIcon, PersonIcon } from '@radix-ui/react-icons';
import { useSmartHubFeed } from './useSmartHubFeed';
import TaskBar from './TaskBar';
import AckDrawer from './AckDrawer';
import AiInsightsBand from './ai/AiInsightsBand';
import type { BulkAckGroup, BulkAckItem, SmartHubItem, SmartHubSubmissionGroup } from './types';
import { isAnswer, isEhrChange, isReading, toBulkAckItem } from './types';
import { smartHubApi } from '../../services/api';

interface SmartHubPanelProps {
  organizationId?: string;
  // PHA-509: when set, the feed is filtered to this patient and the panel
  // shows a "patient context" header so the clinician knows the rail is
  // scoped to the chart they're viewing.
  patientId?: string;
  patientName?: string;
  // Stretch the card to the column's height (the patient-list rail). Off in
  // the chart's sticky, scrolling rail: there a 100% height squeezes the card
  // to the rail's max-height and its overflow:hidden inner clips the feed, so
  // the rail has nothing left to scroll.
  fillHeight?: boolean;
}

const SmartHubPanel: React.FC<SmartHubPanelProps> = ({
  organizationId,
  patientId,
  patientName,
  fillHeight = true,
}) => {
  const {
    taskBar,
    billing,
    billingDenied,
    enabledPrograms,
    loading,
    lastUpdated,
    error,
    refetchTaskBar,
  } = useSmartHubFeed(organizationId, patientId);
  // PHA-479: target is either a single SmartHub row OR a submission group
  // (when the user clicks "Acknowledge all" inside Recent patient answers).
  const [target, setTarget] = useState<SmartHubItem | SmartHubSubmissionGroup | null>(
    null,
  );

  // PHA-678: bulk acknowledge state. The selection can span multiple patients
  // (global selection bar), so we hold a list of per-patient groups and fire
  // one bulkAcknowledge call — one 60-second billing event — per patient.
  const [bulkAckPending, setBulkAckPending] = useState<{ groups: BulkAckGroup[] } | null>(
    null,
  );
  const [bulkAckSubmitting, setBulkAckSubmitting] = useState(false);
  const [bulkAckError, setBulkAckError] = useState<string | null>(null);

  const handleBulkAcknowledge = (groups: BulkAckGroup[]) => {
    if (groups.length === 0) return;
    setBulkAckError(null);
    setBulkAckPending({ groups });
  };

  const confirmBulkAck = async () => {
    if (!bulkAckPending) return;
    setBulkAckSubmitting(true);
    setBulkAckError(null);
    try {
      // One call per patient — idempotent set-once on the backend, so a
      // partial retry is safe.
      for (const group of bulkAckPending.groups) {
        const bulkItems = group.items
          .map(toBulkAckItem)
          .filter((i): i is BulkAckItem => i !== null);
        if (bulkItems.length === 0) continue;
        await smartHubApi.bulkAcknowledge({
          patient_id: group.patientId,
          items: bulkItems,
        });
      }
      setBulkAckPending(null);
      // The acked items vanish from the refetched feed; TaskBar's prune effect
      // then drops them from the selection, clearing the bar.
      await refetchTaskBar();
    } catch (e) {
      console.error('PHA-678: bulk acknowledge failed', e);
      setBulkAckError('Failed to acknowledge items. Please try again.');
    } finally {
      setBulkAckSubmitting(false);
    }
  };

  // Confirmation-dialog summary across all selected patients.
  const bulkAckItemCount = bulkAckPending
    ? bulkAckPending.groups.reduce((acc, g) => acc + g.items.length, 0)
    : 0;
  const bulkAckPatientCount = bulkAckPending ? bulkAckPending.groups.length : 0;
  const bulkAckSummary = bulkAckPending
    ? (() => {
        const counts: Record<string, number> = {};
        for (const group of bulkAckPending.groups) {
          for (const item of group.items) {
            if (isReading(item)) counts['reading'] = (counts['reading'] || 0) + 1;
            else if (isAnswer(item)) counts['survey answer'] = (counts['survey answer'] || 0) + 1;
            else if (isEhrChange(item)) counts['EHR change'] = (counts['EHR change'] || 0) + 1;
          }
        }
        return Object.entries(counts)
          .map(([type, count]) => `${count} ${count === 1 ? type : type + 's'}`)
          .join(', ');
      })()
    : '';

  return (
    <>
      <Card className="shadow-sm" style={fillHeight ? { height: '100%' } : { flexShrink: 0 }}>
        <Tabs.Root defaultValue="smart_hub">
          <Tabs.List className="border-b border-slate-200">
            <Tabs.Trigger value="smart_hub" className="flex items-center gap-2 px-4 py-3">
              <LightningBoltIcon width="16" height="16" />
              Smart Hub
            </Tabs.Trigger>
          </Tabs.List>

          <Tabs.Content value="smart_hub" className="p-6">
            <Flex direction="column" gap="4">
              {patientId && (
                <Flex align="center" gap="2" wrap="wrap">
                  <Badge color="blue" variant="soft" size="2">
                    <PersonIcon width="12" height="12" />
                    Patient context
                  </Badge>
                  {patientName && (
                    <Text size="2" weight="medium" className="text-slate-700">
                      {patientName}
                    </Text>
                  )}
                </Flex>
              )}
              {lastUpdated && (
                <Text size="1" color="gray">
                  Updated{' '}
                  {lastUpdated.toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </Text>
              )}

              {error && (
                <Text size="1" color="red">
                  {error}
                </Text>
              )}

              {/* PHA-567 / PHA-574: AI Insights band sits ABOVE TaskBar.
                  Stacking rule: the backend overlay sets `live_signal` on
                  any AI insight whose patient also appears in today's
                  live acute buckets, bumping that card to the top of the
                  band with a "● LIVE" pip and risk_tier=critical override.
                  TaskBar still renders out_of_bounds + hospitalizations
                  first internally — together those guarantee live acute
                  reads as the top item regardless of the nightly score. */}
              <AiInsightsBand organizationId={organizationId} patientId={patientId} />

              <TaskBar
                feed={taskBar}
                billing={billing}
                billingDenied={billingDenied}
                enabledPrograms={enabledPrograms}
                loading={loading}
                onSelect={setTarget}
                onSelectSubmission={setTarget}
                onBulkAcknowledge={handleBulkAcknowledge}
              />
            </Flex>
          </Tabs.Content>
        </Tabs.Root>
      </Card>

      <AckDrawer
        target={target}
        onClose={() => setTarget(null)}
        onAcknowledged={async () => {
          await refetchTaskBar();
        }}
      />

      <AlertDialog.Root
        open={bulkAckPending !== null}
        onOpenChange={(open) => {
          if (!open && !bulkAckSubmitting) setBulkAckPending(null);
        }}
      >
        <AlertDialog.Content style={{ maxWidth: 460 }}>
          <AlertDialog.Title>Acknowledge items</AlertDialog.Title>
          <AlertDialog.Description size="2" color="gray">
            Acknowledge {bulkAckItemCount}{' '}
            {bulkAckItemCount === 1 ? 'item' : 'items'}{' '}
            {bulkAckPatientCount === 1 ? (
              <>
                for{' '}
                <Text weight="medium">
                  {bulkAckPending?.groups[0]?.patientName}
                </Text>
              </>
            ) : (
              <>
                across <Text weight="medium">{bulkAckPatientCount} patients</Text>
              </>
            )}
            ?
            <br />
            <Text size="1" color="gray" mt="1" as="span">
              {bulkAckSummary}
            </Text>
          </AlertDialog.Description>
          {bulkAckError && (
            <Text size="1" color="red" mt="2" as="p">
              {bulkAckError}
            </Text>
          )}
          <Flex gap="3" mt="6" justify="end">
            <AlertDialog.Cancel>
              <Button variant="soft" color="gray">
                Cancel
              </Button>
            </AlertDialog.Cancel>
            <AlertDialog.Action>
              <Button
                variant="solid"
                color="green"
                onClick={confirmBulkAck}
                disabled={bulkAckSubmitting}
              >
                {bulkAckSubmitting ? 'Acknowledging…' : 'Acknowledge'}
              </Button>
            </AlertDialog.Action>
          </Flex>
        </AlertDialog.Content>
      </AlertDialog.Root>
    </>
  );
};

export default SmartHubPanel;
